// @flow
import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import * as moment from 'moment';
import axios from 'axios';
import QRCode from 'qrcode.react';
import Button from '@material-ui/core/Button';
import classes from "../invoice/Invoice.module.css";
import {
	MACHINE_ORDER,
} from '../../constants';
import { showErrorMsg } from '../../actions/snackbar';
import { clearRealtimeStatuses } from '../../actions/orders';
import useRealtimeConnection from '../../hooks/useRealtimeConnection';
import config from '../../config';

const useStyles = makeStyles((theme) => ({
	qrCodeText: {
		color: '#f05b83',
		fontSize: 14,
		textAlign: 'center',
		padding: '0 20px',
		'& div:nth-child(2)': {
			fontSize: 16,
			fontWeight: 600,
		},
		'& a:last-child': {
			cursor: 'pointer',
			textDecoration: 'underline',
			color: 'inherit',
		},
	},
	accessCode: {
		fontSize: 14,
		fontWeight: 'bold',
		textAlign: 'center',
		margin: '0 0 14px',
		color: '#3e3e3e',
		lineHeight: '18px',
		'& > section:first-child': {
			marginBottom: 4,
		},
	},
}));

type Props = {
	orderDetails: any,
	isOrderActive: Function,
	selectedPod: any,
	accessCode: string,
	bQRViewer: boolean,
	bServer_Order: boolean,
	bVending_Order: boolean,
	openDoorProcessing: boolean,
	setOpenDoorProcessing: Function,
	handleSelectOrder: Function,
	history: any,
};

function MachinePickupSection({
	orderDetails,
	isOrderActive,
	selectedPod,
	accessCode,
	bQRViewer,
	bServer_Order,
	bVending_Order,
	openDoorProcessing,
	setOpenDoorProcessing,
	handleSelectOrder,
	history,
}: Props) {
	const styles = useStyles();
	const dispatch = useDispatch();

	// Add real-time connection

	const { sendStartOrder, sendProcessOrder, connected } = useRealtimeConnection({
		orderId: orderDetails?.order_id,
		machineImei: orderDetails?.vm_imei_id,
		autoConnect: true
	});

	// Debug log to show what props are received
	console.log('🔧 MachinePickupSection props received:', {
		orderId: orderDetails?.order_id,
		hasHistory: !!history,
		hasHandleSelectOrder: !!handleSelectOrder,
		openDoorProcessing,
		hasSetOpenDoorProcessing: !!setOpenDoorProcessing,
		parentComponent: history ? 'likely PaymentSuccess' : 'likely InvoicePage'
	});

	const handleOpenDoor = async () => {
		console.log('🚪 MachinePickupSection handleOpenDoor called:', {
			orderId: orderDetails.order_id,
			hasHistory: !!history,
			historyType: typeof history,
			historyKeys: history ? Object.keys(history) : 'no history',
			machineImei: orderDetails.vm_imei_id,
			connected: connected
		});

		setOpenDoorProcessing(true);
		dispatch(clearRealtimeStatuses());

		try {
			// Step 1: Send start-order command first (10-second timeout)
			console.log('🚀 Sending start-order command...');
			const startOrderResponse = await sendStartOrder(
				orderDetails.order_id,
				bVending_Order,
				{ timeout: 10 }
			);

			console.log('📡 Start-order response:', startOrderResponse);

			// Step 2: Check start-order response
			if (startOrderResponse.status === 'success') {
				console.log('✅ Start-order successful, proceeding with server API...');
				await proceedWithServerAPI();
			} else {
				// Show Pi's error message
				console.error('❌ Start-order failed:', startOrderResponse.message);
				dispatch(showErrorMsg(startOrderResponse.message || 'Machine rejected the order request', 4000));
				setOpenDoorProcessing(false);
				return;
			}

		} catch (error) {
			console.error('🔴 Start-order error:', error);

			if (error.type === 'COMMAND_TIMEOUT') {
				dispatch(showErrorMsg('Machine busy with other order or unreachable. Try later', 4000));
			} else if (error.type === 'CONNECTION_ERROR' || error.type === 'CONNECTION_LOST') {
				dispatch(showErrorMsg('Connection lost. Please refresh and try again.', 4000));
			} else if (error.type === 'CONFIGURATION_ERROR') {
				dispatch(showErrorMsg('Machine configuration error. Please contact support.', 4000));
			} else {
				dispatch(showErrorMsg('Failed to communicate with machine. Please try again.', 3000));
			}

			setOpenDoorProcessing(false);
		}
	};

	// Extract existing server API logic
	const proceedWithServerAPI = async () => {
		try {
			const processOrderUrl = `${config.PHP_API_URL}/tm/process-order/status/${orderDetails.order_id}?__md=1`;
			console.log('📞 Calling process order URL:', processOrderUrl);

			const response = await axios.get(processOrderUrl);
			console.log('✅ Process order URL called successfully:', response.data);

			const responseStatus = response.data?.response?.status;
			if (responseStatus && responseStatus !== true) {
				console.log('⚠️ Process order error:', responseStatus);
				const errorMsg = response.data?.response?.msg || responseStatus;
				dispatch(showErrorMsg(errorMsg, 4000));
				setOpenDoorProcessing(false);
				return;
			}

			// Send fire-and-forget process-order command to Pi
			console.log('🔥 Sending process-order command to Pi...');
			sendProcessOrder(orderDetails.order_id, bVending_Order, orderDetails.access_code);

			handleSelectOrder(orderDetails);
			const targetUrl = `/orders/${orderDetails.order_id}/process`;

			if (!history || !history.push) {
				console.error('🔴 No history object provided!');
				setOpenDoorProcessing(false);
				return;
			}

			console.log('🎯 Navigating to:', targetUrl);
			history.push(targetUrl);
		} catch (error) {
			console.error('🔴 Error calling process order URL:', error);
			dispatch(showErrorMsg("Failed to process order. Please try again.", 3000));
			setOpenDoorProcessing(false);
		}
	};

	// Only render for machine orders that are active
	if (orderDetails.type_of_order !== MACHINE_ORDER || !isOrderActive(orderDetails)) {
		return null;
	}

	return (
		<section>
			{/* Regular Machine Orders (not TM08202103) */}
			{orderDetails.vm_imei_id !== 'TM08202103' && (
				<React.Fragment>
					{/* QR Code Display */}
					{bQRViewer && (
						<section className={styles.qrCodeText} id='qrcode'>
							<section style={{ alignSelf: 'center', marginBottom: 6 }}>
								Show this QR code on the machine tab (touchless experience)
							</section>
							<QRCode value={accessCode} size={250} />
							<div style={{ margintop: 4 }}>OR</div>
						</section>
					)}

					{/* Open Door Button Section */}
					{bServer_Order && (
						<section className={classes.qrCodeText} id='qrcode'>
							<section style={{ alignSelf: 'center', marginBottom: 6 }}>
								Please get near the machine and click open Door
							</section>


							<section>
								<Button
									variant='contained'
									aria-label='Support'
									style={{
										background: (openDoorProcessing || !connected) ? '#ccc' : '#bfd730',
										font: 'bold',
										textDecoration: 'none',
										lineHeight:'42px',
										marginTop: '15px',
										marginBottom: '20px',
										boxShadow: '0px 1px 5px 0px',
										width: '250px',
										minWidth: '250px',
										color: (openDoorProcessing || !connected) ? '#666' : 'black',
										border_radius: '5px',
										cursor: (openDoorProcessing || !connected) ? 'not-allowed' : 'pointer'
									}}
									onClick={handleOpenDoor}
									disabled={openDoorProcessing || !connected}
								>
									{openDoorProcessing ? "Processing..." : (
										!connected ? "Connecting..." : (
											bVending_Order ? "Tap to Vend" : "Open Door"
										)
									)}
								</Button>
							</section>
						</section>
					)}

					{/* Access Code with Server Order */}
					{bServer_Order && (
						<section className={styles.accessCode}>
							<div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
								Selected Machine: {selectedPod.location}
							</div>
							<div style={{ color: '#f05b83' }}>
								Enter access code: <b>{orderDetails.access_code}</b>
							</div>
							<div>
								(EXPIRES AT
								{moment(orderDetails.order_modified_at || orderDetails.last_modified_at)
									.add(1, 'h')
									.format(' h:mm a')}
								)
							</div>
						</section>
					)}

					{/* Access Code without Server Order (excluding specific machine IDs) */}
					{!(orderDetails.vm_imei_id === 'TM12202102' || orderDetails.vm_imei_id === 'TM12202103') && !bServer_Order && (
						<section className={styles.accessCode}>
							<div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
								Selected Machine: {selectedPod.location}
							</div>
							<div style={{ color: '#f05b83' }}>
								Enter access code: <b>{orderDetails.access_code}</b>
							</div>
							<div>
								(EXPIRES AT
								{moment(orderDetails.order_modified_at || orderDetails.last_modified_at)
									.add(1, 'h')
									.format(' h:mm a')}
								)
							</div>
						</section>
					)}
				</React.Fragment>
			)}

			{/* Special TM08202103 Machine - Cafe Person */}
			{orderDetails.vm_imei_id === 'TM08202103' && (
				<React.Fragment>
					<section className={styles.accessCode}>
						<div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px' }}>
							Selected Machine: {selectedPod.location}
						</div>
						<div style={{ color: '#f05b83' }}>
							Transaction Code:: <b>{orderDetails.access_code}</b>
						</div>
						<div></div>
						<div>
							<br/>Please show this to the Cafe Person
							<br/>and collect your item
						</div>
					</section>
				</React.Fragment>
			)}
		</section>
	);
}

export default MachinePickupSection;
